import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Use full URL on native apps (no proxy available), relative URL for web
const getBaseURL = () => {
  if (Capacitor.isNativePlatform()) {
    return 'https://mojo.sschuldt.de/api/v1';
  }
  return '/api/v1';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 15000,
});

// Add token to requests. On iOS also tag the request with the platform so the
// backend can refuse purchase-path endpoints (MOJ-48, Apple Guideline 3.1.1).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (Capacitor.getPlatform() === 'ios') {
    config.headers['X-Mojo-Platform'] = 'ios';
  }
  return config;
});

// Give network/timeout failures (e.g. no connectivity) a clear German message,
// since error.response is undefined for these and callers would otherwise show nothing useful.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      error.offlineMessage =
        error.code === 'ECONNABORTED'
          ? 'Zeitüberschreitung. Bitte überprüfe deine Internetverbindung und versuche es erneut.'
          : 'Keine Internetverbindung. Bitte überprüfe deine Verbindung und versuche es erneut.';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: any) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  deleteMyAccount: () => api.delete('/auth/me'),
  verifyEmail: (token: string) => api.get('/auth/verify-email', { params: { token } }),
};

// MOJ-85: a PDF import uploads the file and waits for the model; the 15 s
// default made the browser give up before the server's answer (success or
// error) arrived, which looked like a hang. Five minutes covers a 100-page
// document; the server-side limits answer oversized files within a second.
const PDF_IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

export const trainerApi = {
  getOverviewStats: () => api.get('/trainers/me/overview'),
  getCustomers: (params?: any) => api.get('/trainers/me/customers', { params }),
  getCustomerDetail: (customerId: number) => api.get(`/trainers/me/customers/${customerId}`),
  createCustomer: (data: any) => api.post('/trainers/me/customers', data),
  updateCustomer: (customerId: number, data: any) => api.put(`/trainers/me/customers/${customerId}`, data),
  resetCustomerPassword: (customerId: number, password?: string) =>
    api.post(`/trainers/me/customers/${customerId}/reset-password`, password ? { password } : {}),
  getPlans: () => api.get('/trainers/me/plans'),
  createPlan: (data: any) => api.post('/trainers/me/plans', data),
  getContent: (params?: any) => api.get('/trainers/me/content', { params }),
  createContent: (data: any) => api.post('/trainers/me/content', data),
  updateContent: (contentId: number, data: any) => api.put(`/trainers/me/content/${contentId}`, data),
  deleteContent: (contentId: number) => api.delete(`/trainers/me/content/${contentId}`),
  importPDF: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/trainers/me/content/import-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: PDF_IMPORT_TIMEOUT_MS,
    });
  },
  // MOJ-86: background import. The upload answers within a second with a job;
  // the app polls the job until it is succeeded or failed, so neither the
  // browser nor any proxy has to keep a connection open while Claude works.
  startPdfImport: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/trainers/me/content/import-pdf/jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60 * 1000, // upload of a 23 MB file on a slow connection
    });
  },
  getPdfImportJob: (jobId: number) => api.get(`/trainers/me/content/import-pdf/jobs/${jobId}`),
  getLatestPdfImportJob: () => api.get('/trainers/me/content/import-pdf/jobs/latest'),
  // MOJ-143: the review list. Save creates the selected rows as library items; discard creates nothing.
  savePdfImportReview: (jobId: number, items: Array<{ index: number; selected: boolean; title?: string }>) =>
    api.post(`/trainers/me/content/import-pdf/jobs/${jobId}/save`, { items }),
  discardPdfImportReview: (jobId: number) => api.post(`/trainers/me/content/import-pdf/jobs/${jobId}/discard`),
  analyzePDFMultiple: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/trainers/me/content/analyze-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: PDF_IMPORT_TIMEOUT_MS,
    });
  },
  analyzeLargeBook: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/trainers/me/content/analyze-book', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000, // 10 minutes for large books
    });
  },
  importSelectedExercises: (exercises: any[], additionalTags: string[]) =>
    api.post('/trainers/me/content/import-selected', { exercises, additionalTags }),
  assignPlanToCustomer: (data: any) => api.post('/trainers/me/assign-plan', data),
  // MOJ-36: trainer-defined start order of a customer's open plans (first to last)
  setCustomerPlanOrder: (customerId: number, userPlanIds: number[]) =>
    api.patch(`/trainers/me/customers/${customerId}/plan-order`, { userPlanIds }),
  updateWeek: (weekId: number, data: any) => api.put(`/trainers/me/weeks/${weekId}`, data),
  updateDay: (dayId: number, data: { trainingContentId?: number | null; gameContentId?: number | null; additionalContentId?: number | null }) =>
    api.put(`/trainers/me/days/${dayId}`, data),
  addDay: (weekId: number, data?: { dayName?: string }) =>
    api.post(`/trainers/me/weeks/${weekId}/days`, data || {}),
  deleteDay: (dayId: number) =>
    api.delete(`/trainers/me/days/${dayId}`),
  deletePlan: (planId: number) =>
    api.delete(`/trainers/me/plans/${planId}`),

  // Customer Requests / Messages
  getRequests: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/trainers/me/requests', { params }),
  getRequestDetail: (requestId: number) =>
    api.get(`/trainers/me/requests/${requestId}`),
  respondToRequest: (requestId: number, message: string) =>
    api.post(`/trainers/me/requests/${requestId}/respond`, { message }),
  updateRequestStatus: (requestId: number, status: string) =>
    api.patch(`/trainers/me/requests/${requestId}/status`, { status }),

  // Training day photos
  getCustomerPhoto: (photoId: number) =>
    api.get(`/trainers/me/customers/photos/${photoId}`, { responseType: 'blob' }),
};

export const customerApi = {
  // Plans
  getMyPlans: () => api.get('/customers/me/plans'),
  getPlanProgress: (userPlanId: number) => api.get(`/customers/me/plans/${userPlanId}/progress`),
  saveProgress: (data: any) => api.post('/customers/me/progress', data),
  saveRepeatProgress: (data: { repeatId: number; rating: number; notes?: string }) =>
    api.post('/customers/me/progress/repeat', data),
  setStartDate: (userPlanId: number, startDate: string) =>
    api.patch(`/customers/me/plans/${userPlanId}/start-date`, { startDate }),

  // Profile
  getMyProfile: () => api.get('/customers/me/profile'),
  updateMyProfile: (data: {
    firstName: string;
    lastName: string;
    dogName?: string;
    dogBreed?: string;
    dogBirthDate?: string;
    dogGender?: string;
    dogNotes?: string;
    phone?: string;
    address?: string;
    acquisitionSource?: string;
    // Dog-keeping / vaccination intake (MOJ-31) — all optional
    isFirstDog?: boolean | null;
    livingSituation?: string;
    city?: string;
    country?: string;
    dogOwnership?: string;
    trainingSince?: string;
    vaccinationStatus?: string;
  }) => api.put('/customers/me/profile', data),
  uploadProfileImage: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/customers/me/profile/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteProfileImage: () => api.delete('/customers/me/profile/image'),
  getProfileImageUrl: () => '/api/v1/customers/me/profile/image',

  // Training day photos
  uploadProgressPhoto: (dayId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/customers/me/days/${dayId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getProgressPhoto: (photoId: number) =>
    api.get(`/customers/me/photos/${photoId}`, { responseType: 'blob' }),
  deleteProgressPhoto: (photoId: number) =>
    api.delete(`/customers/me/photos/${photoId}`),

  // History & Statistics
  getTrainingHistory: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/customers/me/history', { params }),
  getHistoryStats: () => api.get('/customers/me/history/stats'),

  // Next Steps / Recommendations
  getNextSteps: () => api.get('/customers/me/next-steps'),
  updateNextStepInterest: (nextStepId: number, interest: 'INTERESTED' | 'NOT_NOW' | 'DISMISSED') =>
    api.patch(`/customers/me/next-steps/${nextStepId}/interest`, { interest }),
  reorderNextSteps: (order: Array<{ id: number; priority: number }>) =>
    api.patch('/customers/me/next-steps/reorder', { order }),

  // Requests / Messages
  getRequests: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get('/customers/me/requests', { params }),
  getRequestDetail: (requestId: number) =>
    api.get(`/customers/me/requests/${requestId}`),
  createRequest: (data: { subject: string; message: string; category?: string }) =>
    api.post('/customers/me/requests', data),
  respondToRequest: (requestId: number, message: string) =>
    api.post(`/customers/me/requests/${requestId}/respond`, { message }),
};

export const stripeApi = {
  startCheckout: () => api.post<{ url: string }>('/stripe/checkout'),
  openPortal: () => api.post<{ url: string }>('/stripe/portal'),
  getStatus: () =>
    api.get<{
      status: string;
      periodEnd: string | null;
      hasCustomer: boolean;
      hasSubscription: boolean;
    }>('/stripe/status'),
};

export default api;
